import type { ComponentProps } from "react";
import type {
  PostPublic,
  PostCommentPublic,
} from "@softmax/observatory-client/types";
import type { ForumFeedView, ForumPostView } from "@softmax/web-ux/forum";
import { buildCommentTree } from "@softmax/web-features/community";
import {
  collectionError,
  collectionRefreshing,
} from "@softmax/web-features/data";
import { presentPost, presentComments } from "./forum-presentation";
import type { Vote } from "./forum-room.types";

export function feedState({
  posts,
  ready,
  error,
  accessLost,
  refreshing,
  syncError,
  hasMore,
  loadingMore,
  votes,
  elevatedPrivileges,
}: {
  posts: readonly PostPublic[];
  ready: boolean;
  error: Error | null;
  accessLost: boolean;
  refreshing: boolean;
  syncError: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  votes: Record<string, Vote>;
  elevatedPrivileges: boolean;
}): ComponentProps<typeof ForumFeedView>["state"] {
  if (!ready || accessLost) {
    return error && !refreshing
      ? { status: "error", message: "Couldn't load the forum." }
      : { status: "loading" };
  }

  return {
    status: "ready",
    posts: posts.map((post) =>
      presentPost({ post, elevatedPrivileges, vote: votes[post.id] }),
    ),
    refreshing,
    refreshError: syncError ? "Couldn't refresh the forum." : undefined,
    hasMore,
    loadingMore,
    loadMoreError: syncError ? "Couldn't load older posts." : undefined,
  };
}

type ReadHealth = Parameters<typeof collectionError>[0];

export function postState({
  query,
  comments,
  accessLost,
  refreshing,
  postVote,
  commentVotes,
  viewerUserId,
  elevatedPrivileges,
}: {
  readonly query: {
    readonly data: PostPublic | null | undefined;
    readonly health: ReadHealth;
    readonly isSyncError: boolean;
  };
  readonly comments: {
    readonly data: PostCommentPublic[];
    readonly isReady: boolean;
    readonly health: ReadHealth;
    readonly isSyncError: boolean;
  };
  readonly accessLost: boolean;
  readonly refreshing: boolean;
  readonly postVote: Vote;
  readonly commentVotes: Readonly<Record<string, Vote>>;
  readonly viewerUserId: string | null | undefined;
  readonly elevatedPrivileges: boolean;
}): ComponentProps<typeof ForumPostView>["state"] {
  if (!query.data || !comments.isReady || accessLost) {
    const error =
      collectionError(query.health) || collectionError(comments.health);
    return error && !refreshing
      ? { status: "error", message: "Couldn't load this post." }
      : { status: "loading" };
  }

  return {
    status: "ready",
    post: presentPost({ post: query.data, elevatedPrivileges, vote: postVote }),
    comments: {
      status: "ready",
      records: presentComments({
        nodes: buildCommentTree(comments.data),
        votes: commentVotes,
        viewerUserId,
        elevatedPrivileges,
      }),
    },
    refreshing:
      refreshing ||
      collectionRefreshing(query.health) ||
      collectionRefreshing(comments.health),
    refreshError:
      query.isSyncError || comments.isSyncError
        ? "Couldn't refresh this post."
        : undefined,
  };
}
